import { HttpStatusCode } from '@angular/common/http';
import { DestroyRef, Injectable, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { Make, MakeReadOnlyControllerService } from '@garmin-avcloud/avcloud-fly-web-common/api/aircraft/read';
import {
  OrganizationReadOnlyControllerService,
  OrganizationType,
  OrganizationTypeReadOnlyControllerService
} from '@garmin-avcloud/avcloud-fly-web-common/api/orgs-roles/read';
import {
  MembershipInviteWriteControllerService,
  OrganizationWriteControllerService
} from '@garmin-avcloud/avcloud-fly-web-common/api/orgs-roles/write';
import {
  AbstractOrgSettingsService,
  Member,
  OWNER_ROLE,
  SelectedOrgFunctionsService,
  SelectedOrgService,
  StatusBanner
} from '@garmin-avcloud/avcloud-fly-web-common/orgs';
import { OrgFunction } from '@garmin-avcloud/avcloud-fly-web-common/shared';
import { EMPTY, Observable, catchError, forkJoin, of, switchMap, tap, throwError } from 'rxjs';

type SelectableOrgType = OrganizationType & {
  selected: WritableSignal<boolean>;
};

@Injectable({
  providedIn: 'root'
})
export class OrgSettingsService extends AbstractOrgSettingsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectedOrgService = inject(SelectedOrgService);
  private readonly organizationTypeReadOnlyControllerService = inject(OrganizationTypeReadOnlyControllerService);
  private readonly organizationReadOnlyControllerService = inject(OrganizationReadOnlyControllerService);
  private readonly organizationWriteControllerService = inject(OrganizationWriteControllerService);
  private readonly inviteWriteService = inject(MembershipInviteWriteControllerService);
  private readonly selectedOrgFunctionsService = inject(SelectedOrgFunctionsService);
  private readonly makeReadService = inject(MakeReadOnlyControllerService);

  readonly status = signal<StatusBanner | null>(null);
  readonly isOracleBillingFunctionEnabled = new FormControl<boolean>(false, { nonNullable: true });
  private readonly isOracleBillingFunctionEnabledSignal = toSignal(this.isOracleBillingFunctionEnabled.valueChanges, {
    initialValue: false
  });
  readonly isCustomerAircraftAccessFunctionEnabled = new FormControl<boolean>(false, { nonNullable: true });
  private readonly isCustomerAircraftAccessFunctionEnabledSignal = toSignal(
    this.isCustomerAircraftAccessFunctionEnabled.valueChanges,
    {
      initialValue: false
    }
  );
  readonly isOnePakPromotionEnabled = new FormControl<boolean>(false, { nonNullable: true });
  private readonly isOnePakPromotionEnabledSignal = toSignal(this.isOnePakPromotionEnabled.valueChanges, {
    initialValue: false
  });
  readonly isCloudProvisionedSystemIdFunctionEnabled = new FormControl<boolean>(false, { nonNullable: true });
  private readonly isCloudProvisionedSystemIdFunctionEnabledSignal = toSignal(
    this.isCloudProvisionedSystemIdFunctionEnabled.valueChanges,
    {
      initialValue: false
    }
  );

  readonly orgName = new FormControl<string>('', {
    validators: [Validators.required],
    nonNullable: true
  });
  private readonly orgNameSignal = toSignal(this.orgName.valueChanges, { initialValue: '' });
  readonly oracleAccountNumber = new FormControl<string | null>(null, {
    validators: [Validators.pattern(/^[0-9]+$/)]
  });
  private readonly oracleAccountNumberSignal = toSignal(this.oracleAccountNumber.valueChanges, { initialValue: null });
  readonly allowGlobalInviteClaiming = new FormControl(false, { nonNullable: true });
  private readonly globalInviteClaimingSignal = toSignal(this.allowGlobalInviteClaiming.valueChanges, {
    initialValue: false
  });
  private readonly useCustomDomains = toSignal(this.useCustomDomainList.valueChanges, { initialValue: false });

  readonly basicOrgType = signal<SelectableOrgType>({ id: 3, name: 'Basic', selected: signal(false) });
  readonly nonBasicOrgTypes = signal<SelectableOrgType[]>([]);

  /** All makes available for selection */
  readonly allMakes = signal<Make[]>([]);
  /** Make IDs currently selected for this org's aircraft access */
  readonly selectedMakeIds = signal<number[]>([]);

  readonly selectedMakes = computed(() => this.allMakes().filter((m) => this.selectedMakeIds().includes(m.id!)));
  readonly availableMakes = computed(() =>
    this.allMakes()
      .filter((m) => !this.selectedMakeIds().includes(m.id!))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  );

  private readonly selectedOrgTypes = computed(() => {
    const basicOrgType = this.basicOrgType();
    if (basicOrgType.selected()) {
      return basicOrgType.id == null ? [] : [basicOrgType.id];
    }

    return this.nonBasicOrgTypes().reduce((types, type) => {
      if (type.selected() && type.id != null) types.push(type.id);
      return types;
    }, [] as number[]);
  });

  private readonly baseline = {
    allowedEmailDomains: signal([] as string[]),
    orgName: signal(''),
    oracleAccountNumber: signal(null as string | null),
    isOracleBillingFunctionEnabled: signal(false),
    isOnePakPromotionEnabled: signal(false),
    isCustomerAircraftAccessFunctionEnabled: signal(false),
    isCloudProvisionedSystemIdFunctionEnabled: signal(false),
    allowedGlobalInviteClaiming: signal(false),
    selectedOrgTypes: signal([] as number[]),
    useCustomDomains: signal(false),
    isOemOrDealerOrg: signal(false),
    selectedMakeIds: signal([] as number[])
  };

  // Function availability follows saved organization types, not unsaved checkbox changes.
  readonly isOemOrg = computed(() =>
    this.nonBasicOrgTypes().some(
      (orgType) =>
        orgType.name?.toUpperCase() === 'OEM' &&
        orgType.id != null &&
        this.baseline.selectedOrgTypes().includes(orgType.id)
    )
  );
  readonly isOemOrDealerOrg = computed(
    () =>
      this.isOemOrg() ||
      this.nonBasicOrgTypes().some(
        (orgType) =>
          orgType.name?.toUpperCase() === 'DEALER' &&
          orgType.id != null &&
          this.baseline.selectedOrgTypes().includes(orgType.id)
      )
  );

  readonly unsavedChanges = computed(() => {
    if (this.baseline.orgName() !== this.orgNameSignal()) return true;
    if (this.baseline.oracleAccountNumber() != this.oracleAccountNumberSignal()) return true;
    if (this.baseline.isOracleBillingFunctionEnabled() !== this.isOracleBillingFunctionEnabledSignal()) return true;
    if (this.baseline.isOnePakPromotionEnabled() !== this.isOnePakPromotionEnabledSignal()) return true;
    if (
      this.baseline.isCustomerAircraftAccessFunctionEnabled() !== this.isCustomerAircraftAccessFunctionEnabledSignal()
    ) {
      return true;
    }
    if (
      this.baseline.isCloudProvisionedSystemIdFunctionEnabled() !==
      this.isCloudProvisionedSystemIdFunctionEnabledSignal()
    ) {
      return true;
    }
    if (this.baseline.allowedGlobalInviteClaiming() !== this.globalInviteClaimingSignal()) return true;
    if (this.baseline.selectedOrgTypes().length !== this.selectedOrgTypes().length) return true;
    if (this.baseline.useCustomDomains() !== this.useCustomDomains()) return true;
    for (let i = 0; i < this.selectedOrgTypes().length; i++) {
      if (this.baseline.selectedOrgTypes()[i] !== this.selectedOrgTypes()[i]) {
        return true;
      }
    }
    if (this.baseline.allowedEmailDomains().length !== this.allowedEmailDomains().length) return true;
    for (let i = 0; i < this.allowedEmailDomains().length; i++) {
      if (this.baseline.allowedEmailDomains()[i] !== this.allowedEmailDomains()[i]) {
        return true;
      }
    }

    const baselineMakeIds = [...this.baseline.selectedMakeIds()].sort((a, b) => a - b);
    const currentMakeIds = [...this.selectedMakeIds()].sort((a, b) => a - b);
    if (baselineMakeIds.length !== currentMakeIds.length) return true;
    for (let i = 0; i < currentMakeIds.length; i++) {
      if (baselineMakeIds[i] !== currentMakeIds[i]) return true;
    }

    return false;
  });

  constructor() {
    super();
    this.loadSettings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => console.error('Unable to load org settings')
      });

    // These two effects do the following:
    // 1. If the "Basic" org type is selected, deselect all non-"Basic" org types
    // 2. If any non-"Basic" org type is selected, deselect the "Basic" org type
    //
    // "Basic" is a special, default org type and should not be combined with other
    // org types. On the contrary, multiple non-"Basic" org types may be selected.
    //
    // And yes, this is safe. Both effects trigger one another, but they are opposites
    // so when one triggers the other, the other exits immediately. Also, `untracked`
    // is letting us get around the safety checks of `effect` so we can do this without
    // using the `allowSignalWrites` option.
    effect(() => {
      if (!this.basicOrgType().selected()) return;
      // deselect all non-basic org types since basic is selected
      untracked(() => {
        for (const orgType of this.nonBasicOrgTypes()) {
          orgType.selected.set(false);
        }
      });
    });
    effect(() => {
      if (this.nonBasicOrgTypes().find((t) => t.selected()) == null) return;
      // deselect basic org type since at least one non-basic type is selected
      untracked(() => {
        this.basicOrgType().selected.set(false);
      });
    });

    this.oracleAccountNumber.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value !== null && value.toString() === '') {
        this.oracleAccountNumber.setValue(null);
      }
    });
  }

  override loadSettings(): Observable<never> {
    this.allowedEmailDomains.set([]);
    this.useCustomDomainList.setValue(false);
    this.orgName.reset();
    this.oracleAccountNumber.reset();
    this.isOracleBillingFunctionEnabled.reset();
    this.isOnePakPromotionEnabled.reset();
    this.isCloudProvisionedSystemIdFunctionEnabled.reset();
    this.allowGlobalInviteClaiming.reset();
    this.basicOrgType.set({ id: 3, name: 'Basic', selected: signal(false) });
    this.nonBasicOrgTypes.set([]);
    this.allMakes.set([]);
    this.selectedMakeIds.set([]);
    const uuid = this.selectedOrgService.org()?.uuid;

    if (uuid == null) {
      this.status.set({ type: 'error', message: 'no org selected' });
      return throwError(() => new Error('No org selected'));
    } else {
      // FIXME - yea look at that function name...
      return this.organizationTypeReadOnlyControllerService.getOrganizationTypeList().pipe(
        switchMap((orgTypes) => {
          const nonBasicOrgTypes = [];
          for (const orgType of orgTypes) {
            if (orgType.name === 'Basic') {
              this.basicOrgType.set({ ...orgType, selected: signal(false) });
            } else {
              nonBasicOrgTypes.push({ ...orgType, selected: signal(false) });
            }
          }
          this.nonBasicOrgTypes.set(nonBasicOrgTypes);
          const summaryReq$ = this.organizationReadOnlyControllerService.getOrganizationSummary(uuid);
          const oracleActReq$ = this.organizationReadOnlyControllerService
            .getOrganizationOracleAccountNumber(uuid, 'body', false, { httpHeaderAccept: 'text/plain' as '*/*' })
            .pipe(
              catchError((error) => {
                if (error.status !== HttpStatusCode.NotFound) {
                  this.status.set({ type: 'error', message: 'Failed to retrieve Oracle account number.' });
                }
                return of(null);
              })
            );
          const orgOracleBillingFunctionReq$ = this.selectedOrgFunctionsService
            .isFunctionEnabled(OrgFunction.ORACLE_BILLING)
            .pipe(
              catchError(() => {
                this.status.set({ type: 'error', message: 'Failed to retrieve Oracle Function Setting.' });
                return of(false);
              })
            );
          const isOnePakPromotionFunctionReq$ = this.selectedOrgFunctionsService
            .isFunctionEnabled(OrgFunction.ONEPAK_PROMOTION)
            .pipe(
              catchError(() => {
                this.status.set({ type: 'error', message: 'Failed to retrieve OnePak Promotion Function Setting.' });
                return of(false);
              })
            );
          const isCustomerAircraftAccessFunctionReq$ = this.selectedOrgFunctionsService
            .isFunctionEnabled(OrgFunction.CUSTOMER_AIRCRAFT_ACCESS)
            .pipe(
              catchError(() => {
                this.status.set({
                  type: 'error',
                  message: 'Failed to retrieve Customer Aircraft Access Function Setting.'
                });
                return of(false);
              })
            );
          const isCloudProvisionedSystemIdFunctionReq$ = this.selectedOrgFunctionsService
            .isFunctionEnabled(OrgFunction.CLOUD_SYSTEM_ID_MANAGEMENT)
            .pipe(
              catchError(() => {
                this.status.set({
                  type: 'error',
                  message: 'Failed to retrieve Cloud Provisioned System ID Function Setting.'
                });
                return of(false);
              })
            );

          const makesReq$ = this.makeReadService.getMakes();
          const orgAircraftAccessMakesReq$ =
            this.organizationReadOnlyControllerService.getOrganizationAircraftAccessMakes(uuid);

          return forkJoin([
            summaryReq$,
            oracleActReq$,
            orgOracleBillingFunctionReq$,
            isOnePakPromotionFunctionReq$,
            isCustomerAircraftAccessFunctionReq$,
            isCloudProvisionedSystemIdFunctionReq$,
            makesReq$,
            orgAircraftAccessMakesReq$
          ]);
        }),
        switchMap(
          ([
            orgSummary,
            oracleAccountNumber,
            isOracleBillingFunctionEnabled,
            isOnePakPromotionEnabled,
            isCustomerAircraftAccessFunctionEnabled,
            isCloudProvisionedSystemIdFunctionEnabled,
            makes,
            orgAircraftAccessMakes
          ]) => {
            this.oracleAccountNumber.setValue(oracleAccountNumber);
            this.isOracleBillingFunctionEnabled.setValue(isOracleBillingFunctionEnabled);
            this.isOnePakPromotionEnabled.setValue(isOnePakPromotionEnabled);
            this.isCustomerAircraftAccessFunctionEnabled.setValue(isCustomerAircraftAccessFunctionEnabled);
            this.isCloudProvisionedSystemIdFunctionEnabled.setValue(isCloudProvisionedSystemIdFunctionEnabled);
            this.allowedEmailDomains.set(orgSummary.allowedEmailDomains ?? []);
            this.useCustomDomainList.setValue(this.allowedEmailDomains().length > 0);
            this.orgName.setValue(orgSummary.name ?? '');
            this.allowGlobalInviteClaiming.setValue(orgSummary.allowGlobalInviteClaiming ?? false);
            for (const orgType of orgSummary.types ?? []) {
              if (orgType.name === 'Basic') {
                this.basicOrgType().selected.set(true);
              } else {
                this.nonBasicOrgTypes()
                  .find((t) => t.id === orgType.id)
                  ?.selected.set(true);
              }
            }
            this.allMakes.set(makes);
            const orgMakeIds = orgAircraftAccessMakes.makeIds ?? [];
            this.selectedMakeIds.set(orgMakeIds);
            this.baseline.selectedMakeIds.set([...orgMakeIds]);
            this.baseline.allowedEmailDomains.set(this.allowedEmailDomains());
            this.baseline.selectedOrgTypes.set(this.selectedOrgTypes());
            this.baseline.allowedGlobalInviteClaiming.set(this.allowGlobalInviteClaiming.value);
            this.baseline.orgName.set(this.orgName.value);
            this.baseline.oracleAccountNumber.set(this.oracleAccountNumber.value);
            this.baseline.isOracleBillingFunctionEnabled.set(this.isOracleBillingFunctionEnabled.value);
            this.baseline.isOnePakPromotionEnabled.set(this.isOnePakPromotionEnabled.value);
            this.baseline.isCustomerAircraftAccessFunctionEnabled.set(
              this.isCustomerAircraftAccessFunctionEnabled.value
            );
            this.baseline.isCloudProvisionedSystemIdFunctionEnabled.set(
              this.isCloudProvisionedSystemIdFunctionEnabled.value
            );
            this.baseline.useCustomDomains.set(this.useCustomDomainList.value);
            return EMPTY;
          }
        )
      );
    }
  }

  save(): Observable<never> {
    const org = this.selectedOrgService.org();
    if (org == null || org.uuid == null) {
      this.status.set({ type: 'error', message: 'no org selected' });
      return throwError(() => new Error('No org selected'));
    }

    if (this.orgName.invalid) {
      this.status.set({ type: 'error', message: 'invalid org name entry' });
      return throwError(() => new Error('Invalid org name entry'));
    }

    if (this.oracleAccountNumber.invalid) {
      this.status.set({ type: 'error', message: 'Invalid oracle account number' });
      return throwError(() => new Error('Invalid oracle account number'));
    }
    org.name = this.orgName.value;
    org.allowGlobalInviteClaiming = this.allowGlobalInviteClaiming.value;

    const allowedEmailDomains = this.useCustomDomainList.value ? this.allowedEmailDomains() : [];
    const allowedEmailDomains$ = this.organizationWriteControllerService
      .updateOrganizationAllowedEmailDomains(org.uuid, allowedEmailDomains)
      .pipe(
        tap((domains) => {
          this.allowedEmailDomains.set(domains);
          this.baseline.allowedEmailDomains.set(domains);
          this.baseline.useCustomDomains.set(this.useCustomDomainList.value);
        })
      );
    const org$ = this.organizationWriteControllerService.updateOrganization(org.uuid, org).pipe(
      tap(() => {
        this.baseline.orgName.set(org.name ?? '');
        this.selectedOrgService.refresh();
        this.baseline.allowedGlobalInviteClaiming.set(this.allowGlobalInviteClaiming.value);
      })
    );
    const orgType$ = this.organizationWriteControllerService
      .updateOrganizationTypes(org.uuid, this.selectedOrgTypes())
      .pipe(
        tap(() => {
          this.baseline.selectedOrgTypes.set(this.selectedOrgTypes());
        })
      );

    const orgOracleActNumber$ =
      this.oracleAccountNumberSignal() === this.baseline.oracleAccountNumber()
        ? of(null)
        : this.organizationWriteControllerService
            .updateOrganizationOracleAccountNumber(org.uuid, this.oracleAccountNumberSignal()!, 'body', false, {
              httpHeaderAccept: 'text/plain' as '*/*'
            })
            .pipe(
              tap(() => {
                this.baseline.oracleAccountNumber.set(this.oracleAccountNumberSignal());
              })
            );

    const orgFunctionOverride$ =
      this.isOracleBillingFunctionEnabledSignal() === this.baseline.isOracleBillingFunctionEnabled() &&
      this.isOnePakPromotionEnabledSignal() === this.baseline.isOnePakPromotionEnabled() &&
      this.isCustomerAircraftAccessFunctionEnabledSignal() ===
        this.baseline.isCustomerAircraftAccessFunctionEnabled() &&
      this.isCloudProvisionedSystemIdFunctionEnabledSignal() ===
        this.baseline.isCloudProvisionedSystemIdFunctionEnabled()
        ? of(null)
        : this.organizationReadOnlyControllerService.getOrganizationFunctionOverrides(org.uuid).pipe(
            switchMap((functionOverrides) => {
              const updateOracleBillingFunction: boolean =
                this.isOracleBillingFunctionEnabledSignal() !== this.baseline.isOracleBillingFunctionEnabled();
              const updateOnePakPromotionFunction: boolean =
                this.isOnePakPromotionEnabledSignal() !== this.baseline.isOnePakPromotionEnabled();
              const updateCustomerAircraftAccessFunction: boolean =
                this.isCustomerAircraftAccessFunctionEnabledSignal() !==
                this.baseline.isCustomerAircraftAccessFunctionEnabled();
              const updateCloudProvisionedSystemIdFunction: boolean =
                this.isCloudProvisionedSystemIdFunctionEnabledSignal() !==
                this.baseline.isCloudProvisionedSystemIdFunctionEnabled();
              const updatedFunctionOverrides = functionOverrides
                .filter(
                  (override) => !updateOracleBillingFunction || override.functionId !== OrgFunction.ORACLE_BILLING
                )
                .filter(
                  (override) => !updateOnePakPromotionFunction || override.functionId !== OrgFunction.ONEPAK_PROMOTION
                )
                .filter(
                  (override) =>
                    !updateCustomerAircraftAccessFunction ||
                    override.functionId !== OrgFunction.CUSTOMER_AIRCRAFT_ACCESS
                )
                .filter(
                  (override) =>
                    !updateCloudProvisionedSystemIdFunction ||
                    override.functionId !== OrgFunction.CLOUD_SYSTEM_ID_MANAGEMENT
                )
                .map((override) => {
                  return {
                    allowed: override.allowed,
                    functionId: override.functionId
                  };
                });

              if (updateOracleBillingFunction) {
                updatedFunctionOverrides.push({
                  allowed: this.isOracleBillingFunctionEnabledSignal(),
                  functionId: OrgFunction.ORACLE_BILLING
                });
              }

              if (updateOnePakPromotionFunction) {
                updatedFunctionOverrides.push({
                  allowed: this.isOnePakPromotionEnabledSignal(),
                  functionId: OrgFunction.ONEPAK_PROMOTION
                });
              }

              if (updateCustomerAircraftAccessFunction) {
                updatedFunctionOverrides.push({
                  allowed: this.isCustomerAircraftAccessFunctionEnabledSignal(),
                  functionId: OrgFunction.CUSTOMER_AIRCRAFT_ACCESS
                });
              }

              if (updateCloudProvisionedSystemIdFunction) {
                updatedFunctionOverrides.push({
                  allowed: this.isCloudProvisionedSystemIdFunctionEnabledSignal(),
                  functionId: OrgFunction.CLOUD_SYSTEM_ID_MANAGEMENT
                });
              }

              return this.organizationWriteControllerService.updateOrganizationFunctionOverrides(
                org.uuid!,
                updatedFunctionOverrides,
                'body',
                false
              );
            }),
            tap(() => {
              this.baseline.isOracleBillingFunctionEnabled.set(this.isOracleBillingFunctionEnabledSignal());
              this.baseline.isOnePakPromotionEnabled.set(this.isOnePakPromotionEnabledSignal());
              this.baseline.isCustomerAircraftAccessFunctionEnabled.set(
                this.isCustomerAircraftAccessFunctionEnabledSignal()
              );
              this.baseline.isCloudProvisionedSystemIdFunctionEnabled.set(
                this.isCloudProvisionedSystemIdFunctionEnabledSignal()
              );
            }),
            switchMap(() => this.selectedOrgFunctionsService.load(true))
          );

    return forkJoin([allowedEmailDomains$, org$, orgType$, orgOracleActNumber$, orgFunctionOverride$]).pipe(
      tap({ error: () => this.status.set({ type: 'error', message: 'unable to save settings' }) }),
      switchMap(() => {
        const makeIds = this.selectedMakeIds();
        return this.organizationWriteControllerService.setOrganizationAircraftAccessMakes(org.uuid!, { makeIds }).pipe(
          tap((result) => {
            const savedIds = result.makeIds ?? [];
            this.selectedMakeIds.set(savedIds);
            this.baseline.selectedMakeIds.set([...savedIds]);
          })
        );
      }),
      switchMap(() => EMPTY)
    );
  }

  // To transfer ownership an invite is sent with the owner role then immediately accepted, removing the original owner and replacing them with the new owner
  transfer(recipientMember: Member): Observable<never> {
    const organizationUuid = this.selectedOrgService.org()?.uuid;
    if (organizationUuid == null) {
      this.status.set({ type: 'error', message: 'no org selected' });
      return throwError(() => new Error('No org selected'));
    } else {
      return this.inviteWriteService
        .createMembershipInvite({
          organizationUuid,
          recipientEmailAddress: recipientMember.email,
          roleUuids: [OWNER_ROLE.uuid]
        })
        .pipe(
          switchMap((invite) =>
            this.inviteWriteService.acceptMembershipInvite(invite.uuid!, { customerGuid: recipientMember.customerGuid })
          )
        ) // invite.uuid cannot be null if returning from backend after create
        .pipe(switchMap(() => EMPTY));
    }
  }
}
